import { findNikkeInCatalog } from '../../nikke-catalog/domain/nikkeCatalog.js';
import {
  canonicalTetrisDeckKeys,
  canonicalTetrisMemberKey,
  canonicalTetrisNikkeKey,
  tetrisDeckDisplayCodes,
} from './raidTetrisIdentity.js';

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function text(value) {
  return String(value ?? '').trim();
}

function memberKey(value = {}) {
  return canonicalTetrisMemberKey(value);
}

function canonicalDeckKeysWithCatalog(value = [], catalog = null) {
  const source = Array.isArray(value) ? value.slice(0, 5) : [];
  return source.map((item) => {
    const displayCode = tetrisDeckDisplayCodes([item])[0] || item;
    const catalogItem = findNikkeInCatalog(catalog, displayCode);
    return catalogItem?.nikkeId || canonicalTetrisNikkeKey(item);
  }).filter(Boolean);
}

export function buildRaidPhaseTargets({ bosses = [], globalPhase = 1 } = {}) {
  const currentPhase = Math.max(1, Math.min(4, numberValue(globalPhase, 1)));
  const targets = [];

  for (let level = 1; level <= 3; level += 1) {
    for (const boss of Array.isArray(bosses) ? bosses : []) {
      const bossNo = numberValue(boss.bossNo);
      if (bossNo < 1 || bossNo > 5) continue;
      const phase = (Array.isArray(boss.phases) ? boss.phases : []).find((item) => numberValue(item.phaseNo) === level) || null;
      const hp = Math.max(0, numberValue(phase?.maxHpEok));
      let remaining = hp;
      if (level < currentPhase || phase?.cleared) remaining = 0;
      else if (level === currentPhase) remaining = Math.max(0, numberValue(phase?.remainingHpEok, boss.remainingHpEok));

      targets.push({
        id: `${level}-${bossNo}`,
        level,
        bossNo,
        bossName: text(boss.bossName) || `${bossNo}보스`,
        element: text(boss.element),
        hp,
        remaining,
        alreadyCleared: remaining <= 0,
        order: (level - 1) * 5 + bossNo - 1,
      });
    }
  }

  return targets.sort((a, b) => a.order - b.order);
}

export function buildRaidSimulationCandidates(candidates = [], catalog = null) {
  return (Array.isArray(candidates) ? candidates : []).map((candidate) => {
    const rawDeck = Array.isArray(candidate.deck) && candidate.deck.length
      ? candidate.deck
      : candidate.canonicalDeck;
    return {
      ...candidate,
      memberKey: memberKey(candidate),
      damage: numberValue(candidate.damageEok ?? candidate.damage),
      deckKeys: catalog
        ? canonicalDeckKeysWithCatalog(rawDeck, catalog)
        : canonicalTetrisDeckKeys(rawDeck),
    };
  });
}

export function buildActualRaidUsage(records = [], catalog = null) {
  const initialUsage = new Map();
  const initialDeckKeys = new Map();

  for (const record of Array.isArray(records) ? records : []) {
    const key = memberKey(record);
    if (!key) continue;
    initialUsage.set(key, Math.min(3, (initialUsage.get(key) || 0) + 1));
    const used = initialDeckKeys.get(key) || new Set();
    const rawDeck = record.deck ?? record.deck_codes ?? record.deckCodes;
    const deckKeys = catalog
      ? canonicalDeckKeysWithCatalog(rawDeck, catalog)
      : canonicalTetrisDeckKeys(rawDeck);
    deckKeys.forEach((code) => used.add(code));
    initialDeckKeys.set(key, used);
  }

  return { initialUsage, initialDeckKeys };
}

export function buildRaidFullSimulationInput({
  season = null,
  candidates = [],
  members = [],
  catalog = null,
  fitMode = 'kill',
  allowedErrorEok = 0,
} = {}) {
  const actualRecords = Array.isArray(season?.records) ? season.records : [];
  const { initialUsage, initialDeckKeys } = buildActualRaidUsage(actualRecords, catalog);
  return {
    targets: buildRaidPhaseTargets({ bosses: season?.bosses || [], globalPhase: season?.globalPhase || 1 }),
    candidates: buildRaidSimulationCandidates(candidates, catalog),
    initialUsage,
    initialDeckKeys,
    memberCount: Math.max((Array.isArray(members) ? members : []).length, initialUsage.size),
    beamWidth: 36,
    partialWidth: 180,
    planLimit: 3,
    combosPerTarget: 4,
    maxPerMemberBoss: 5,
    maxCandidatesPerTarget: 56,
    fitMode: fitMode === 'nearest' ? 'nearest' : 'kill',
    allowedErrorEok: Math.max(0, numberValue(allowedErrorEok)),
  };
}
